import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Plus, User, Package, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useHaptics } from '../../hooks/useHaptics';

interface BottomNavProps {
    showSellButton?: boolean;
}

const BottomNav: React.FC<BottomNavProps> = ({ showSellButton = true }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { selection, medium } = useHaptics();
    const { currentUser } = useAuth();
    const [isPressing, setIsPressing] = useState<string | null>(null);

    const isActive = (path: string) => location.pathname === path;

    const navItems = [
        { id: 'home', label: 'Keşfet', icon: Home, path: '/' },
        { id: 'transactions', label: 'Varlıklar', icon: Package, path: '/dashboard' },
        { id: 'sell', label: 'Varlık Listele', icon: Plus, path: '/sell', isPrimary: true },
        { id: 'chat', label: 'Hizmet', icon: MessageCircle, path: '/messages' },
        { id: 'profile', label: 'Profil', icon: User, path: `/profile/${currentUser?.id || ''}` }
    ];

    const handleNavigation = (path: string, id: string) => {
        if (id === 'sell') medium(); else selection();

        const protectedRoutes = ['sell', 'chat', 'profile', 'transactions'];
        if (protectedRoutes.includes(id) && !currentUser) {
            navigate('/auth');
            return;
        }
        navigate(path);
    };

    return (
        <nav className="fixed bottom-6 left-4 right-4 z-50 md:hidden pointer-events-none">
            <div className="max-w-lg mx-auto pointer-events-auto">
                {/* Floating Pill Container with Smoky Glass */}
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="relative px-2 h-16 bg-white/70 dark:bg-gray-900/80 backdrop-blur-2xl rounded-full border border-brand-accent/10 dark:border-gray-800 shadow-[0_20px_40px_rgba(0,0,0,0.1)] flex items-center justify-around overflow-hidden"
                >
                    {navItems.map((item) => {
                        const active = isActive(item.path);

                        if (item.isPrimary) {
                            return (
                                <div key={item.id} className="relative z-20">
                                    <button
                                        onPointerDown={() => setIsPressing(item.id)}
                                        onPointerUp={() => setIsPressing(null)}
                                        onClick={() => handleNavigation(item.path, item.id)}
                                        className="w-12 h-12 bg-brand-primary text-brand-accent rounded-full flex items-center justify-center shadow-[0_4px_12px_rgba(212,175,55,0.2)] active:scale-90 transition-all duration-300 relative"
                                    >
                                        <Plus size={28} strokeWidth={2.5} />
                                        {/* Pure Liquid Expansion on Long Press */}
                                        <AnimatePresence>
                                            {isPressing === item.id && (
                                                <motion.div
                                                    layoutId="liquid-expansion"
                                                    initial={{ scale: 0.8, opacity: 0 }}
                                                    animate={{ scale: 1.5, opacity: 0.4 }}
                                                    exit={{ scale: 2, opacity: 0 }}
                                                    className="absolute inset-0 bg-brand-primary/20 rounded-full blur-xl -z-10"
                                                />
                                            )}
                                        </AnimatePresence>
                                    </button>
                                </div>
                            );
                        }

                        return (
                            <button
                                key={item.id}
                                onPointerDown={() => setIsPressing(item.id)}
                                onPointerUp={() => setIsPressing(null)}
                                onClick={() => handleNavigation(item.path, item.id)}
                                className={`relative flex flex-col items-center justify-center p-2 flex-1 h-full transition-all duration-300 active:scale-90 ${active ? 'text-brand-accent' : 'text-brand-clay/40 dark:text-gray-500'}`}
                            >
                                {/* Liquid Transition Indicator */}
                                {active && (
                                    <motion.div
                                        layoutId="liquid-nav-bg"
                                        transition={{
                                            type: "spring",
                                            stiffness: 300,
                                            damping: 30,
                                            mass: 1.2
                                        }}
                                        className="absolute inset-x-1.5 inset-y-2 bg-gray-100/80 dark:bg-gray-800/80 rounded-2xl -z-10 border border-gray-200/50 dark:border-gray-700/50"
                                    />
                                )}

                                <motion.div
                                    animate={active ? {
                                        scale: [1, 1.2, 0.95, 1],
                                        rotate: [0, 5, -5, 0]
                                    } : {}}
                                    transition={{ duration: 0.4 }}
                                    className="relative flex items-center justify-center"
                                >
                                    <item.icon
                                        size={22}
                                        strokeWidth={active ? 2.5 : 1.5}
                                        className="transition-all duration-300"
                                    />
                                    {active && (
                                        <motion.div
                                            layoutId="active-dot"
                                            className="absolute -bottom-1 w-1 h-1 bg-brand-accent rounded-full"
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                        />
                                    )}
                                </motion.div>

                                <span className={`text-[9px] mt-1 font-bold tracking-tight transition-all duration-300 ${active ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 hidden'}`}>
                                    {item.label}
                                </span>
                            </button>
                        );
                    })}
                </motion.div>
            </div>
        </nav>
    );
};

export default BottomNav;
